import _objectWithoutProperties from "/vercel/path0/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["id", "visibleIn"];
import _styled from "styled-components";
import { useMemo } from 'react';
import Image from 'next/image';
import { BuildingOfficeIcon, MapPinIcon, UsersIcon, EllipsisHorizontalIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { EyeSlashIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { customImageLoader, domainInterestOptions, getEmployerJPState } from '@restworld/utils-common';
import { Badge } from '@restworld/ui-ds-base';
import { IconButton, Dropdown } from '@restworld/ui-ds-base';
import { Title } from '../title';
import { ImageSlider } from '../image-slider';
import Paragraph from '../paragraph';
import { V2Link } from '../../feature-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function getJPOnboardingFormLink(_ref) {
  var jpId = _ref.jpId,
      titleDescriptionSet = _ref.titleDescriptionSet,
      jpState = _ref.jpState;
  var baseLink = "/employer/job-positions/onboarding?job_position_id=".concat(jpId);

  switch (jpState) {
    case 'pending_approval':
      baseLink += "&step=review";
      break;

    case 'draft':
      if (titleDescriptionSet) {
        baseLink += "&step=review";
      }

      break;

    default:
      break;
  }

  return baseLink;
}
export function EmployerCard(_ref2) {
  var children = _ref2.children,
      href = _ref2.href,
      onClick = _ref2.onClick,
      twStyles = _ref2.twStyles;
  return /*#__PURE__*/_jsx(CardLinkContainer, {
    href: href,
    onClick: onClick,
    $twStyles: twStyles,
    prefetch: true,
    children: children
  });
}
export default EmployerCard;
export function EmployerCardHeader(_ref3) {
  var logo = _ref3.logo,
      title = _ref3.title,
      badge = _ref3.badge,
      statusKey = _ref3.statusKey,
      jpId = _ref3.jpId,
      occupationLabel = _ref3.occupationLabel,
      onRequestChanges = _ref3.onRequestChanges,
      onDuplicate = _ref3.onDuplicate,
      onDeleteDraft = _ref3.onDeleteDraft,
      onPutOffline = _ref3.onPutOffline;

  var _useTranslation = useTranslation('job-positions-page'),
      t = _useTranslation.t;

  var router = useRouter();
  var jpState = useMemo(function () {
    return getEmployerJPState(statusKey);
  }, [statusKey]);
  var allMenuItems = useMemo(function () {
    return [{
      id: 'edit',
      label: t('jp_menu_edit'),
      Icon: PencilSquareIcon,
      onClick: function onClick() {
        // Since right now, there is a 'success' step as the last one, so hardcoding 'review' here otherwise user will be redirected to 'success' step
        jpId && router.push(getJPOnboardingFormLink({
          jpId: jpId,
          titleDescriptionSet: !!title,
          jpState: jpState
        }));
      },
      visibleIn: ['draft', 'pending_approval']
    }, {
      id: 'request_changes',
      label: t('jp_menu_request_changes'),
      Icon: PencilSquareIcon,
      onClick: function onClick() {
        onRequestChanges === null || onRequestChanges === void 0 ? void 0 : onRequestChanges();
      },
      visibleIn: ['open']
    }, {
      id: 'duplicate',
      label: t('jp_menu_duplicate'),
      Icon: DocumentDuplicateIcon,
      onClick: function onClick() {
        jpId && (onDuplicate === null || onDuplicate === void 0 ? void 0 : onDuplicate(jpId));
      },
      visibleIn: ['closed', 'open', 'pending_approval', 'draft']
    }, {
      id: 'delete_draft',
      label: t('jp_menu_delete_draft'),
      Icon: TrashIcon,
      onClick: function onClick() {
        jpId && (onDeleteDraft === null || onDeleteDraft === void 0 ? void 0 : onDeleteDraft(jpId));
      },
      isCriticalAction: true,
      visibleIn: ['draft']
    }, {
      id: 'cancel_approval',
      label: t('jp_menu_cancel_approval'),
      Icon: TrashIcon,
      onClick: function onClick() {
        jpId && (onDeleteDraft === null || onDeleteDraft === void 0 ? void 0 : onDeleteDraft(jpId));
      },
      isCriticalAction: true,
      visibleIn: ['pending_approval']
    }, {
      id: 'put_offline',
      label: t('jp_menu_put_offline'),
      Icon: EyeSlashIcon,
      onClick: function onClick() {
        jpId && (onPutOffline === null || onPutOffline === void 0 ? void 0 : onPutOffline(jpId));
      },
      isCriticalAction: true,
      visibleIn: ['open']
    }];
  }, [t, router, jpId, jpState, onRequestChanges, onDuplicate, onDeleteDraft, onPutOffline, title]);
  var menuItems = useMemo(function () {
    return allMenuItems.filter(function (item) {
      return item.visibleIn.includes(jpState);
    }).map(function (_ref4) {
      var id = _ref4.id,
          visibleIn = _ref4.visibleIn,
          item = _objectWithoutProperties(_ref4, _excluded);

      return item;
    });
  }, [allMenuItems, jpState]);

  var logoElement = logo && /*#__PURE__*/_jsx(LogoWrapper, {
    children: /*#__PURE__*/_jsx(_StyledImage, {
      src: logo,
      alt: "",
      fill: true,
      loader: customImageLoader
    })
  });

  var titleElement = /*#__PURE__*/_jsx(Title, {
    fontWeight: "medium",
    fontSize: "extraLarge",
    twStyle: {
      "maxWidth": "100%",
      "fontSize": "1.25rem",
      "lineHeight": "1.75rem"
    },
    children: title || (jpState === 'draft' ? t('jp_draft_of_search', {
      role: occupationLabel
    }) : null)
  });

  var badgeElement = badge && /*#__PURE__*/_jsx(BadgeWrapper, {
    children: /*#__PURE__*/_jsx(Badge, {
      variant: badge.variant,
      children: badge.text
    })
  });

  var menuElement = /*#__PURE__*/_jsx("div", {
    onClick: function onClick(e) {
      e.stopPropagation();
      e.preventDefault();
    },
    children: /*#__PURE__*/_jsx(Dropdown, {
      items: menuItems,
      triggerElement: /*#__PURE__*/_jsx(IconButton, {
        ariaLabel: "more options",
        Icon: EllipsisHorizontalIcon
      }),
      disabled: menuItems.length === 0
    })
  });

  return /*#__PURE__*/_jsxs(CardHeaderWrapper, {
    children: [/*#__PURE__*/_jsxs(LogoAndMenuWrapper, {
      children: [/*#__PURE__*/_jsx("div", {
        children: logoElement
      }), /*#__PURE__*/_jsx("div", {
        children: menuElement
      })]
    }), /*#__PURE__*/_jsx(TitleMobileWrapper, {
      children: titleElement
    }), /*#__PURE__*/_jsx(BadgeMobileWrapper, {
      children: badgeElement
    }), /*#__PURE__*/_jsxs(LogoAndTitleWrapper, {
      children: [logoElement, /*#__PURE__*/_jsx(TitleWrapper, {
        children: titleElement
      })]
    }), /*#__PURE__*/_jsxs(HeaderActionsWrapper, {
      children: [badgeElement, menuElement]
    })]
  });
}
export function EmployerCardImages(_ref5) {
  var images = _ref5.images;
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(ImageSlider, {
      images: images
    })
  });
}
export function EmployerCardInfo(_ref6) {
  var info = _ref6.info;

  var _useTranslation2 = useTranslation('common'),
      translateCommom = _useTranslation2.t;

  var DomainInterestIcon = useMemo(function () {
    var _domainInterestOption;

    return (_domainInterestOption = domainInterestOptions.find(function (option) {
      return option.key === info.domainInterestKey;
    })) === null || _domainInterestOption === void 0 ? void 0 : _domainInterestOption.iconElement;
  }, [info.domainInterestKey]);
  return /*#__PURE__*/_jsx(CardInfoWrapper, {
    children: InfoItems.map(function (_ref7) {
      var key = _ref7.key,
          icon = _ref7.icon;
      var value = info[key];
      return value ? /*#__PURE__*/_jsx(InfoItem, {
        icon: key === 'occupation' && DomainInterestIcon ? /*#__PURE__*/_jsx(DomainInterestIcon, {
          className: "h-4 w-4"
        }) : icon,
        text: "".concat(value, " ").concat(key === 'openPositions' ? translateCommom('people_wanted') : '')
      }, key) : null;
    })
  });
}
export function EmployerCardFooter(_ref8) {
  var labels = _ref8.labels,
      notification = _ref8.notification;
  var hasLabels = !!(labels !== null && labels !== void 0 && labels.length);
  return /*#__PURE__*/_jsxs(CardFooterWrapper, {
    children: [!!(labels !== null && labels !== void 0 && labels.length) && /*#__PURE__*/_jsx(LabelsWrapper, {
      children: labels.map(function (_ref9) {
        var text = _ref9.text,
            _ref9$variant = _ref9.variant,
            variant = _ref9$variant === void 0 ? 'info' : _ref9$variant;
        return /*#__PURE__*/_jsx(Badge, {
          variant: variant,
          children: text
        }, text);
      })
    }), !!(notification !== null && notification !== void 0 && notification.candidateCount) && (notification === null || notification === void 0 ? void 0 : notification.message) && notification.candidates && /*#__PURE__*/_jsx(NotificationSectionWrapper, {
      hasLabels: hasLabels,
      children: /*#__PURE__*/_jsx(NewCandidateNotification, {
        candidates: notification.candidates,
        message: notification.message,
        candidateCount: notification.candidateCount,
        isAllSpontaneous: notification.isAllSpontaneous
      })
    })]
  });
}

function InfoItem(_ref10) {
  var icon = _ref10.icon,
      text = _ref10.text;
  return /*#__PURE__*/_jsxs(InfoItemWrapper, {
    children: [/*#__PURE__*/_jsx(IconWrapper, {
      children: icon
    }), /*#__PURE__*/_jsx(Paragraph, {
      font: "medium",
      standardVariant: "body-sm",
      color: "gray-500",
      children: text
    })]
  });
}

export function NewCandidateNotification(_ref11) {
  var isAllSpontaneous = _ref11.isAllSpontaneous,
      message = _ref11.message,
      wrapperStyle = _ref11.wrapperStyle,
      imageWrapperStyle = _ref11.imageWrapperStyle,
      _ref11$candidateCount = _ref11.candidateCount,
      candidateCount = _ref11$candidateCount === void 0 ? 0 : _ref11$candidateCount,
      _ref11$candidates = _ref11.candidates,
      candidates = _ref11$candidates === void 0 ? [] : _ref11$candidates,
      _ref11$imageDisplayLi = _ref11.imageDisplayLimit,
      imageDisplayLimit = _ref11$imageDisplayLi === void 0 ? 3 : _ref11$imageDisplayLi,
      _ref11$leftOffset = _ref11.leftOffset,
      leftOffset = _ref11$leftOffset === void 0 ? 16 : _ref11$leftOffset;
  var moreToShowCount = candidateCount && candidateCount - imageDisplayLimit;
  var hasMoreToShow = !!moreToShowCount && moreToShowCount > 0;
  var textLeftOffset = (Math.min(candidateCount, imageDisplayLimit) - (candidateCount === 2 || candidateCount === 3 ? 1 : 0)) * leftOffset - 0;
  return candidates !== null && candidates !== void 0 && candidates.length ? /*#__PURE__*/_jsxs(NotificationWrapper, {
    isSpontaneous: isAllSpontaneous,
    twStyle: wrapperStyle,
    children: [/*#__PURE__*/_jsxs(_StyledDiv, {
      children: [candidates.slice(0, imageDisplayLimit).map(function (candidate, i) {
        return /*#__PURE__*/_jsx(RoundedWrapper, {
          isPositioned: i > 0,
          twStyles: [{
            left: i === 1 ? 18 : i * leftOffset
          }, imageWrapperStyle || {}],
          children: candidate.image ? /*#__PURE__*/_jsx(_StyledImage2, {
            src: candidate.image,
            alt: "",
            width: 28,
            height: 28,
            loader: customImageLoader
          }) : /*#__PURE__*/_jsx(Title, {
            fontSize: "base",
            fontWeight: "semibold",
            twStyle: [{
              "display": "grid",
              "height": "1.75rem",
              "width": "1.75rem",
              "placeItems": "center",
              "borderRadius": "9999px",
              "--tw-bg-opacity": "1",
              "backgroundColor": "rgb(240 235 248 / var(--tw-bg-opacity))",
              "fontSize": "0.875rem",
              "lineHeight": "1.25rem",
              "textTransform": "uppercase",
              "--tw-text-opacity": "1",
              "color": "rgb(72 36 133 / var(--tw-text-opacity))"
            }],
            children: "".concat(candidate !== null && candidate !== void 0 && candidate.firstName ? candidate.firstName[0] : '').concat(candidate !== null && candidate !== void 0 && candidate.lastName ? candidate.lastName[0] : '')
          })
        }, i);
      }), !!hasMoreToShow && /*#__PURE__*/_jsx(RoundedWrapper, {
        isPositioned: true,
        twStyles: [{
          left: imageDisplayLimit * leftOffset
        }, imageWrapperStyle || {}],
        children: /*#__PURE__*/_jsx(Title, {
          fontSize: "base",
          fontWeight: "semibold",
          twStyle: [{
            "display": "grid",
            "height": "1.75rem",
            "width": "1.75rem",
            "placeItems": "center",
            "borderRadius": "9999px",
            "--tw-bg-opacity": "1",
            "backgroundColor": "rgb(246 245 255 / var(--tw-bg-opacity))",
            "fontSize": "0.875rem",
            "lineHeight": "1.25rem",
            "--tw-text-opacity": "1",
            "color": "rgb(105 62 181 / var(--tw-text-opacity))"
          }],
          children: "+".concat(moreToShowCount)
        })
      })]
    }), !!message && /*#__PURE__*/_jsx(Paragraph, {
      font: "medium",
      standardVariant: "body-sm",
      color: "white",
      twStyle: {
        marginLeft: candidateCount > 1 ? textLeftOffset : 0
      },
      children: message
    })]
  }) : null;
}
var InfoIconMap = {
  address: /*#__PURE__*/_jsx(MapPinIcon, {
    className: "h-4 w-4"
  }),
  businessName: /*#__PURE__*/_jsx(BuildingOfficeIcon, {
    className: "h-4 w-4"
  }),
  occupation: /*#__PURE__*/_jsx(BuildingOfficeIcon, {
    className: "h-4 w-4"
  }),
  openPositions: /*#__PURE__*/_jsx(UsersIcon, {
    className: "h-4 w-4"
  })
};
var InfoItems = [{
  key: 'businessName',
  icon: InfoIconMap.businessName
}, {
  key: 'occupation',
  icon: InfoIconMap.occupation
}, {
  key: 'address',
  icon: InfoIconMap.address
}, {
  key: 'openPositions',
  icon: InfoIconMap.openPositions
}];

var CardLinkContainer = /*#__PURE__*/_styled(V2Link).withConfig({
  displayName: "employer-card__CardLinkContainer",
  componentId: "sc-hzsrl2-0"
})(["", " ", ""], {
  "boxSizing": "border-box",
  "display": "flex",
  "flexDirection": "column",
  "gap": "1rem",
  "borderRadius": "1rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(250 250 250 / var(--tw-bg-opacity))",
  "padding": "1.25rem",
  "transitionProperty": "all",
  "transitionTimingFunction": "cubic-bezier(0.4, 0, 0.2, 1)",
  "transitionDuration": "150ms",
  ":hover": {
    "--tw-border-opacity": "1",
    "borderColor": "rgb(102 51 187 / var(--tw-border-opacity))",
    "--tw-shadow": "inset 0 0 0 1px #6633bb",
    "--tw-shadow-colored": "inset 0 0 0 1px var(--tw-shadow-color)",
    "boxShadow": "var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)"
  }
}, function (_ref12) {
  var $twStyles = _ref12.$twStyles;
  return $twStyles || [];
});

var CardHeaderWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardHeaderWrapper",
  componentId: "sc-hzsrl2-1"
})(["", ""], {
  "display": "flex",
  "maxWidth": "100%",
  "flexDirection": "column",
  "gap": "0.5rem",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "alignItems": "center",
    "justifyContent": "space-between"
  }
});

var LogoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LogoWrapper",
  componentId: "sc-hzsrl2-2"
})(["", ""], {
  "position": "relative",
  "display": "flex",
  "height": "2rem",
  "width": "2rem",
  "minWidth": "2rem",
  "alignItems": "center",
  "justifyContent": "center",
  "overflow": "hidden",
  "borderRadius": "0.5rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "objectFit": "cover",
  "objectPosition": "center"
});

var LogoAndMenuWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LogoAndMenuWrapper",
  componentId: "sc-hzsrl2-3"
})(["", ""], {
  "display": "flex",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "0.5rem",
  "@media (min-width: 768px)": {
    "display": "none"
  }
});

var TitleMobileWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__TitleMobileWrapper",
  componentId: "sc-hzsrl2-4"
})(["", ""], {
  "maxWidth": "100%",
  "@media (min-width: 768px)": {
    "display": "none"
  }
});

var BadgeMobileWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__BadgeMobileWrapper",
  componentId: "sc-hzsrl2-5"
})(["", ""], {
  "@media (min-width: 768px)": {
    "display": "none"
  }
});

var LogoAndTitleWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LogoAndTitleWrapper",
  componentId: "sc-hzsrl2-6"
})(["", ""], {
  "display": "none",
  "@media (min-width: 640px)": {
    "width": "100%"
  },
  "@media (min-width: 768px)": {
    "display": "flex",
    "width": "75%",
    "maxWidth": "75%",
    "alignItems": "center",
    "gap": "0.5rem"
  },
  "@media (min-width: 1536px)": {
    "maxWidth": "62%"
  }
});

var TitleWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__TitleWrapper",
  componentId: "sc-hzsrl2-7"
})(["", ""], {
  "maxWidth": "100%"
});

var HeaderActionsWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__HeaderActionsWrapper",
  componentId: "sc-hzsrl2-8"
})(["", ""], {
  "display": "none",
  "@media (min-width: 768px)": {
    "display": "flex",
    "alignItems": "center",
    "gap": "0.5rem"
  }
});

var BadgeWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__BadgeWrapper",
  componentId: "sc-hzsrl2-9"
})(["", ""], {
  "minWidth": "fit-content",
  "maxWidth": "fit-content",
  "whiteSpace": "nowrap",
  "textTransform": "lowercase"
});

var CardInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardInfoWrapper",
  componentId: "sc-hzsrl2-10"
})(["", ""], {
  "display": "flex",
  "flexWrap": "wrap",
  "gap": "0.5rem"
});

var CardFooterWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardFooterWrapper",
  componentId: "sc-hzsrl2-11"
})(["", ""], {
  "display": "flex",
  "flexDirection": "column",
  "gap": "1rem",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "alignItems": "center"
  }
});

var NotificationSectionWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__NotificationSectionWrapper",
  componentId: "sc-hzsrl2-12"
})(["", " ", ""], {
  "display": "flex",
  "flex": "1 1 0%",
  "borderRadius": "9999px"
}, function (_ref13) {
  var hasLabels = _ref13.hasLabels;
  return hasLabels ? {
    "@media (min-width: 768px)": {
      "justifyContent": "flex-end"
    }
  } : {
    "@media (min-width: 768px)": {
      "justifyContent": "flex-start"
    }
  };
});

var LabelsWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LabelsWrapper",
  componentId: "sc-hzsrl2-13"
})(["", ""], {
  "display": "flex",
  "flex": "1 1 0%",
  "flexWrap": "wrap",
  "gap": "0.5rem",
  "textTransform": "lowercase"
});

var InfoItemWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__InfoItemWrapper",
  componentId: "sc-hzsrl2-14"
})(["", ""], {
  "display": "flex",
  "width": "fit-content",
  "flexWrap": "nowrap",
  "alignItems": "center",
  "gap": "0.25rem"
});

var IconWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__IconWrapper",
  componentId: "sc-hzsrl2-15"
})(["", ""], {
  "height": "1rem",
  "width": "1rem"
});

var RoundedWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__RoundedWrapper",
  componentId: "sc-hzsrl2-16"
})(function (_ref14) {
  var twStyles = _ref14.twStyles;
  return [{
    "display": "grid",
    "height": "2rem",
    "width": "2rem",
    "placeItems": "center",
    "borderRadius": "9999px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
  }, function (_ref15) {
    var isPositioned = _ref15.isPositioned;
    return isPositioned && {
      "position": "absolute"
    };
  }, twStyles || {}];
});

var NotificationWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__NotificationWrapper",
  componentId: "sc-hzsrl2-17"
})(function (_ref16) {
  var isSpontaneous = _ref16.isSpontaneous,
      twStyle = _ref16.twStyle;
  return [{
    "position": "relative",
    "display": "flex",
    "minHeight": "40px",
    "width": "fit-content",
    "alignItems": "center",
    "gap": "0.5rem",
    "overflow": "hidden",
    "textOverflow": "ellipsis",
    "whiteSpace": "nowrap",
    "borderRadius": "9999px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(102 51 187 / var(--tw-bg-opacity))",
    "padding": "0.25rem",
    "paddingRight": "0.75rem"
  }, twStyle || {}];
});

var _StyledImage = /*#__PURE__*/_styled(Image).withConfig({
  displayName: "employer-card___StyledImage",
  componentId: "sc-hzsrl2-18"
})({
  "objectFit": "cover",
  "objectPosition": "center"
});

var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  displayName: "employer-card___StyledDiv",
  componentId: "sc-hzsrl2-19"
})({
  "display": "flex"
});

var _StyledImage2 = /*#__PURE__*/_styled(Image).withConfig({
  displayName: "employer-card___StyledImage2",
  componentId: "sc-hzsrl2-20"
})({
  "height": "1.75rem",
  "width": "1.75rem",
  "borderRadius": "9999px",
  "objectFit": "cover",
  "objectPosition": "center"
});